import { useCallback, useRef, useState } from "react";
import { getDomainFilterDefaults } from "../context/envDefaults";
import categoryApiService from "../services/CategoryApiService";
import { DEFAULT_CATEGORY_QUERY_THRESHOLD } from "../utils";

// Sections shown collapsed/expanded in the reused domain FiltersSidebar
const DEFAULT_EXPANDED_SECTIONS = {
	mediaSubtype: false,
	deviceTypes: false,
	distributionChannel: false,
	videoPlacement: false,
	dateRange: false,
	geography: false,
	contentStyle: false,
	safetyTier: false,
	directPublishers: false,
	publishers: false,
	relevance: false,
	performance: false,
};

export const useCategorySearch = () => {
	const [subTab, setSubTab] = useState("domains"); // "domains" | "apps"

	// Search input state (same pattern as other tabs)
	const [searchInput, setSearchInput] = useState("");
	// Threshold sheet: Categories -> Strong Match (the sheet's default tier)
	const [currentThreshold, setCurrentThreshold] = useState(
		DEFAULT_CATEGORY_QUERY_THRESHOLD,
	);

	// Terms (chips) — each: { id, text, threshold, type: 'include'|'exclude' }
	const [searchTerms, setSearchTerms] = useState([]);

	// Results
	const [results, setResults] = useState([]);
	const [totalRecords, setTotalRecords] = useState(0);
	const [keywords, setKeywords] = useState([]);
	const [isSearching, setIsSearching] = useState(false);
	const [error, setError] = useState(null);
	const [hasSearched, setHasSearched] = useState(false);

	// Filters use the SAME shape as the Domain UI so the domain FiltersSidebar can be reused
	const [filters, setFilters] = useState(() => getDomainFilterDefaults());
	const [expandedSections, setExpandedSections] = useState(
		DEFAULT_EXPANDED_SECTIONS,
	);

	// Sort (category default = relevance)
	const [sortBy, setSortBy] = useState("relevance");
	const [sortDirection, setSortDirection] = useState("DESC");

	// Pagination (client-side slicing)
	const [currentPage, setCurrentPage] = useState(1);
	const pageSize = 25;

	const latestRef = useRef({});
	latestRef.current = { subTab, searchTerms, filters, sortBy, sortDirection };

	// ---- term management ----
	const addSearchTerm = useCallback((text, threshold, type) => {
		const trimmed = text.trim();
		if (!trimmed) return;
		setSearchTerms((prev) => [
			...prev,
			{
				id: Date.now(),
				text: trimmed,
				threshold: threshold ?? DEFAULT_CATEGORY_QUERY_THRESHOLD,
				type,
			},
		]);
		setSearchInput("");
	}, []);

	const removeSearchTerm = useCallback((id) => {
		setSearchTerms((prev) => prev.filter((t) => t.id !== id));
	}, []);

	const clearSearchTerms = useCallback(() => {
		setSearchTerms([]);
		setSearchInput("");
	}, []);

	// ---- filter handlers (same semantics as the Domain tab) ----
	const updateFilter = useCallback((key, value) => {
		setFilters((prev) => ({ ...prev, [key]: value }));
	}, []);

	const toggleArrayFilter = useCallback((key, value) => {
		setFilters((prev) => {
			const currentArray = prev[key] || [];
			return {
				...prev,
				[key]: currentArray.includes(value)
					? currentArray.filter((item) => item !== value)
					: [...currentArray, value],
			};
		});
	}, []);

	const toggleSection = useCallback((section) => {
		setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
	}, []);

	const clearAllFilters = useCallback(() => {
		// Clear to empty selections (matching the Domain tab), NOT back to
		// getDomainFilterDefaults() — the defaults still preselect mediaSubtype,
		// deviceTypes, country, language, etc., so resetting to them looks like
		// "Clear All" did nothing. Spread the defaults to preserve structural
		// fields (dateRange, limit, customFilters shape) then empty the selections.
		const defaults = getDomainFilterDefaults();
		setFilters({
			...defaults,
			mediaSubtype: [],
			deviceTypes: [],
			distributionChannel: [],
			videoPlacement: [],
			geography: [],
			language: [],
			contentStyle: [],
			content_style_1: [],
			content_style_2: [],
			content_style_3: [],
			safetyTier: [],
			safety_tier_1: [],
			safety_tier_2: [],
			safety_tier_3: [],
			isDirect: false,
			publishers: [],
			selectedCountries: [],
			categories: [],
			minViewability: 0,
			minCTR: 0,
			minReach: 0,
			relevanceThreshold: 0,
			minReachThreshold: 0,
			minViewabilityThreshold: 0,
			minCTRVTR: 0,
			customFilters: (defaults.customFilters || []).map((f) => ({
				...f,
				threshold: 0,
			})),
		});
	}, []);

	// ---- search ----
	const search = useCallback(async () => {
		const { subTab, searchTerms, filters, sortBy, sortDirection } =
			latestRef.current;

		const includeTerms = searchTerms.filter((t) => t.type === "include");
		if (!includeTerms.length) return;

		const excludeTerms = searchTerms.filter((t) => t.type === "exclude");

		setIsSearching(true);
		setError(null);

		const params = {
			queries: includeTerms.map((t) => ({ query: t.text, threshold: t.threshold })),
			excludeQueries: excludeTerms.map((t) => ({ query: t.text, threshold: t.threshold })),
			filters,
			sortBy,
			view: sortDirection,
		};

		const result =
			subTab === "domains"
				? await categoryApiService.searchDomainsByCategory(params)
				: await categoryApiService.searchAppsByCategory(params);

		setIsSearching(false);
		setHasSearched(true);
		setCurrentPage(1);

		if (result.success) {
			const data = result.data;
			setResults(data.domain ?? data.data ?? []);
			setTotalRecords(data.totalRecords ?? data.totalCount ?? 0);
			setKeywords(Array.isArray(data.keywords) ? [...data.keywords] : []);
			setError(null);
		} else {
			setResults([]);
			setTotalRecords(0);
			setKeywords([]);
			setError(result.error || "Search failed. Please try again.");
		}
	}, []);

	// ---- Inject results from the AI Search dropdown (search.jsx) ----
	// payload.appliedFilters is the full filters object from the NLP DomainSearchRequest —
	// map it back to the domain UI shape so the filter sidebar reflects what the AI chose.
	const injectResults = useCallback((payload) => {
		if (!payload) return;
		setSubTab(payload.subTab || "domains");
		setSearchTerms(Array.isArray(payload.searchTerms) ? payload.searchTerms : []);
		setResults(Array.isArray(payload.results) ? payload.results : []);
		setTotalRecords(payload.totalRecords || 0);
		setKeywords(Array.isArray(payload.keywords) ? payload.keywords : []);
		setError(null);
		setHasSearched(true);
		setIsSearching(false);
		setCurrentPage(1);

		if (payload.appliedFilters) {
			const f = payload.appliedFilters;
			setFilters((prev) => ({
				...prev,
				selectedCountries: f.domainFilters?.geography ?? prev.selectedCountries,
				mediaSubtype: f.domainFilters?.mediaSubtype ?? prev.mediaSubtype,
				deviceTypes: f.domainFilters?.deviceTypes ?? prev.deviceTypes,
				distributionChannel: f.domainFilters?.distributionChannel ?? prev.distributionChannel,
				videoPlacement: f.domainFilters?.videoplcmt ?? prev.videoPlacement,
				contentStyle: f.domainFilters?.contentStyle ?? prev.contentStyle,
				publishers: f.domainFilters?.publisher ?? prev.publishers,
				isDirect: f.domainFilters?.isDirect ?? prev.isDirect,
				safetyTier: f.vectorFilters?.safetyTier ?? prev.safetyTier,
				categories: f.vectorFilters?.iab_category ?? prev.categories,
				minViewability: f.performance?.minViewability != null
					? f.performance.minViewability * 100
					: prev.minViewability,
				minReach: f.performance?.minReach ?? prev.minReach,
				limit: f.noOfResult ?? prev.limit,
				noOfDays: f.noOfDays ?? prev.noOfDays,
			}));
		}
	}, []);

	const clearAll = useCallback(() => {
		setSearchTerms([]);
		setSearchInput("");
		setResults([]);
		setTotalRecords(0);
		setKeywords([]);
		setError(null);
		setHasSearched(false);
		setCurrentPage(1);
	}, []);

	const handleSubTabChange = useCallback((tab) => {
		setSubTab(tab);
		setResults([]);
		setTotalRecords(0);
		setKeywords([]);
		setError(null);
		setHasSearched(false);
		setCurrentPage(1);
	}, []);

	// Paginated slice
	const paginatedResults = results.slice(
		(currentPage - 1) * pageSize,
		currentPage * pageSize,
	);
	const totalPages = Math.max(1, Math.ceil(results.length / pageSize));

	return {
		subTab,
		onSubTabChange: handleSubTabChange,

		searchInput,
		setSearchInput,
		currentThreshold,
		setCurrentThreshold,

		searchTerms,
		addSearchTerm,
		removeSearchTerm,
		clearSearchTerms,

		isSearching,
		error,
		hasSearched,
		results,
		paginatedResults,
		totalRecords,
		totalPages,
		keywords,
		currentPage,
		pageSize,
		setCurrentPage,

		// Filters (domain UI shape) + handlers for the reused FiltersSidebar
		filters,
		expandedSections,
		updateFilter,
		toggleArrayFilter,
		toggleSection,
		clearAllFilters,

		sortBy,
		setSortBy,
		sortDirection,
		setSortDirection,

		search,
		clearAll,
		injectResults,
	};
};
